import { useState, useEffect, ChangeEvent, useContext } from 'react';

import { useCookie, useGeladaAccessTokenDecoder } from '@netfront/common-library';
import {
  DEFAULT_STORAGE_EXPIRY_OPTION,
  useDomain,
  useGetGeladaProject,
  useIdentitySiteUrls,
  useProtectedRoute,
} from '@netfront/gelada-identity-library';
import { AvatarBreadcrumbSectionTemplate, InformationBox } from '@netfront/ui-library';
import { PageTemplate } from 'components';
import { addMonths } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { REPORTING_PAGE_CONSTANTS } from './ReportingPage.constants';
import { OverallPdfTableView, OverallTranslateTableView, PdfTableView, TranslateTableView } from './ReportingPageViews';

import { PlanContext } from '../../../contexts';
import { useLastProjectVisited, useToast } from '../../../hooks';
import { BUTTON_CLASSES } from '../../LinkButton';
import { getPlanUsagePercentage, getPlanUsageText, PlanUsage } from '../../PlanUsage';

const { pageTitle, tableSelectionItems, viewSelectionItems } = REPORTING_PAGE_CONSTANTS;

const ReportingPage = () => {
  const { createLastProjectVisitedCookie, getAccessTokenCookie, isSecureCookie } = useCookie();
  const { getDomain, isDomainReady } = useDomain();
  const { getDecodedJwt, getJwtUserId } = useGeladaAccessTokenDecoder();
  const { kanziProject } = useContext(PlanContext);
  const { getBaseUrl } = useIdentitySiteUrls({
    environment: process.env.REACT_APP_ENVIRONMENT,
    port: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });
  const { getLastProjectVisitedPath } = useLastProjectVisited();
  const { isAuthenticated } = useProtectedRoute({
    environment: process.env.REACT_APP_ENVIRONMENT,
    identitySitePort: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });
  const {
    query: { organisationKey, projectId },
  } = useRouter();
  const { handleToastError } = useToast();

  const [hasRequiredPageData, setHasRequiredPageData] = useState<boolean>(false);
  const [identityUrl, setIdentityUrl] = useState<string>('');
  const [geladaProjectName, setGeladaProjectName] = useState<string>();
  const [selectedTable, setSelectedTable] = useState<string>(String(tableSelectionItems[0].value));
  const [selectedView, setSelectedView] = useState<string>(String(viewSelectionItems[0].value));
  const [dateRange, setDateRange] = useState<Date[]>([addMonths(new Date(), -1), addMonths(new Date(), 1)]);
  const [userId, setUserId] = useState<number>();

  const accessToken = getAccessTokenCookie();

  const handleTableChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
    setSelectedTable(value);
  };

  const handleViewChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
    setSelectedView(value);
  };

  const { handleGetGeladaProject, isLoading: isGetProjectLoading = false } = useGetGeladaProject({
    fetchPolicy: 'cache-first',
    onCompleted: ({ geladaProject: { name } }) => {
      setGeladaProjectName(name);
      setHasRequiredPageData(true);
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const decodedJwt = getDecodedJwt(accessToken);

    setUserId(getJwtUserId(decodedJwt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!(isAuthenticated && isDomainReady && organisationKey && projectId && userId)) {
      return;
    }

    setIdentityUrl(getBaseUrl());

    createLastProjectVisitedCookie({
      optionalCookieAttributesInput: {
        domain: getDomain(),
        secure: isSecureCookie(process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE),
        storageExpiryOptions: DEFAULT_STORAGE_EXPIRY_OPTION,
      },
      value: getLastProjectVisitedPath(organisationKey, projectId, userId),
    });

    handleGetGeladaProject({
      projectId: String(projectId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isDomainReady, organisationKey, projectId, userId]);

  const isLoading = isGetProjectLoading;

  return (
    <PageTemplate 
      activePage="reporting" 
      isLoading={isLoading} 
      pageTitle={pageTitle}
      topLevelChildren={
        <>
          {hasRequiredPageData && (
            <>
              {kanziProject && (
                <PlanUsage
                  plans={[
                    {
                      color: 'var(--g-color-red-400)',
                      label: 'Translate usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedTranslate, kanziProject.remainingTranslate),
                      usage: getPlanUsageText(kanziProject.allowedTranslate, kanziProject.remainingTranslate),
                    },
                    {
                      color: 'var(--g-color-orange-200)',
                      label: 'Speech usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedSpeech, kanziProject.remainingSpeech),
                      usage: getPlanUsageText(kanziProject.allowedSpeech, kanziProject.remainingSpeech),
                    },
                    {
                      color: 'var(--g-color-blue-200)',
                      label: 'PDF usage',
                      percentage: getPlanUsagePercentage(kanziProject.allowedPdf, kanziProject.remainingPdf),
                      usage: getPlanUsageText(kanziProject.allowedPdf, kanziProject.remainingPdf),
                    },
                  ]}
                />
              )}
            </>
          )}
        </>
      }
    >
      <>
        {hasRequiredPageData && (
          <>
            <div className="flex flex-col mb-8 lg:flex-row lg:items-center">
              {geladaProjectName ? (
                <AvatarBreadcrumbSectionTemplate
                  additionalClassNames="flex-1 mb-8 md:mb-0"
                  breadcrumbItems={[
                    {
                      key: '0',
                      content: (
                        <Link
                          className={`${BUTTON_CLASSES['subtle-link']} color-black`}
                          href={`${identityUrl}/dashboard`}
                        >
                          <span>Dashboard</span>
                        </Link>
                      ),
                    },
                    {
                      key: '1',
                      content: <span>{pageTitle}</span>,
                    },
                  ]}
                  title={geladaProjectName}
                />
              ) : null}
            </div>

            <InformationBox additionalClassNames="mb-8" iconId="id_info_icon" message="Export customised reports" />

            <div className="flex flex-1 flex-wrap -mx-4">
              {/* translate overall view */}
              {selectedTable === String(tableSelectionItems[0].value) && selectedView === String(viewSelectionItems[0].value) && (
                <OverallTranslateTableView
                  dateRange={dateRange}
                  selectedTable={selectedTable}
                  selectedView={selectedView}
                  onDateRangeChange={setDateRange}
                  onTableChange={handleTableChange}
                  onViewChange={handleViewChange}
                />
              )}

              {/* translate breakdown view */}
              {selectedTable === String(tableSelectionItems[0].value) && selectedView === String(viewSelectionItems[1].value) && (
                <TranslateTableView
                  dateRange={dateRange}
                  selectedTable={selectedTable}
                  selectedView={selectedView}
                  onDateRangeChange={setDateRange}
                  onTableChange={handleTableChange}
                  onViewChange={handleViewChange}
                />
              )}

              {/* pdf overall view */}
              {selectedTable === String(tableSelectionItems[1].value) && selectedView === String(viewSelectionItems[0].value) && (
                <OverallPdfTableView
                  dateRange={dateRange}
                  selectedTable={selectedTable}
                  selectedView={selectedView}
                  onDateRangeChange={setDateRange}
                  onTableChange={handleTableChange}
                  onViewChange={handleViewChange}
                />
              )}
              
              {/* pdf breakdown view */}
              {selectedTable === String(tableSelectionItems[1].value) && selectedView === String(viewSelectionItems[1].value) && (
                <PdfTableView
                  dateRange={dateRange}
                  selectedTable={selectedTable}
                  selectedView={selectedView}
                  onDateRangeChange={setDateRange}
                  onTableChange={handleTableChange}
                  onViewChange={handleViewChange}
                />
              )}
            </div>
          </>
        )}
      </>
    </PageTemplate>
  );
};

export { ReportingPage };
